import type { ConflictResolver, RebaseResult } from "./proposal-steps"
import { applyProposalSteps, rebaseProposal } from "./proposal-steps"

// Pure decision functions for the proposal-apply paths. Each takes denormalized
// data (queried by the imperative shell) and returns a `supersede` outcome with
// a reason, or an `apply`/`create`/`delete` outcome with the materialized
// payload. The Zero mutator and the Prisma agent path do the I/O around these —
// the branching itself is pure and fully covered by sync, fixture-based tests.

// Minimal article shape for an EDIT decision. Callers project the fields from
// whatever row type they have (Zero row, Prisma row, test fixture).
export interface ArticleForApply {
	isDeleted: boolean
	title: string
}

// Result of walking the version chain. `baseHtml: null` means the proposal
// can't anchor (its base version row is missing or pruned). `chainSteps: null`
// means at least one hop is missing logged steps; the rebase falls back to a
// snapshot transform via `recreateTransform`. `chainSteps: []` means the
// proposal is already at head and the rebase is a clean replay.
export interface ChainForApply {
	baseHtml: string | null
	currentHtml: string
	chainSteps: unknown[] | null
}

export type EditDecision =
	| { kind: "supersede" }
	| {
			kind: "apply"
			appliedHtml: string
			rebasedSteps: unknown[]
			newTitle: string
	  }

export interface EditDecisionInput {
	article: ArticleForApply | null
	chain: ChainForApply
	stepsJson: unknown[]
	proposedTitle: string | null
	resolver?: ConflictResolver
}

export function decideEditOutcome(input: EditDecisionInput): EditDecision {
	const { article, chain, stepsJson, proposedTitle, resolver } = input
	if (!article || article.isDeleted || chain.baseHtml == null) {
		return { kind: "supersede" }
	}

	let outcome: RebaseResult
	try {
		outcome = rebaseProposal({
			baseHtml: chain.baseHtml,
			currentHtml: chain.currentHtml,
			stepsJson,
			chainSteps: chain.chainSteps,
			resolver,
		})
	} catch {
		return { kind: "supersede" }
	}

	// All steps conflicted → no net change. Supersede instead of landing an
	// empty version row. Compare steps, not HTML: the stored head and the
	// re-serialized result differ in markup even when nothing was applied.
	if (
		outcome.conflicts.length > 0 &&
		(outcome.rebasedSteps.length === 0 ||
			outcome.appliedHtml === chain.currentHtml)
	) {
		return { kind: "supersede" }
	}

	return {
		kind: "apply",
		appliedHtml: outcome.appliedHtml,
		rebasedSteps: outcome.rebasedSteps,
		newTitle: proposedTitle ?? article.title,
	}
}

export type DeleteDecision = { kind: "supersede" } | { kind: "delete" }

export interface DeleteDecisionInput {
	articleId: string | null
	// Caller projects `{ isDeleted }` from the fetched article row, or passes
	// `null` if the article doesn't exist.
	article: { isDeleted: boolean } | null
}

export function decideDeleteOutcome(
	input: DeleteDecisionInput,
): DeleteDecision {
	if (!input.articleId || !input.article || input.article.isDeleted) {
		return { kind: "supersede" }
	}
	return { kind: "delete" }
}

export type CreateDecision =
	| { kind: "supersede" }
	| { kind: "create"; html: string; title: string }

export interface CreateDecisionInput {
	proposedTitle: string | null
	proposedGroupId: string | null
	// `null` when no target group was specified. `false` when a target was
	// specified but the group has been hard-deleted between draft and accept.
	// `true` when the targeted group still exists.
	groupExists: boolean | null
	stepsJson: unknown[]
}

export function decideCreateOutcome(
	input: CreateDecisionInput,
): CreateDecision {
	if (input.proposedGroupId && input.groupExists === false) {
		return { kind: "supersede" }
	}
	try {
		const { html } = applyProposalSteps("", input.stepsJson)
		return {
			kind: "create",
			html,
			title: input.proposedTitle ?? "Untitled",
		}
	} catch {
		return { kind: "supersede" }
	}
}
